import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import GlgInlineLoadingSpinner from '../../../../shared/components/glg_loading_spinner_inline'
import { useGetExistingCoursesQuery } from '../../../api/courses'

const COPY_OPTIONS = [
  { key: 'course_name_abbr', i18nKey: 'courses.crs.popup.course_name_and_abbreviation', col: 0 },
  { key: 'tee_time_interval', i18nKey: 'courses.crs.popup.tee_time_interval', col: 0 },
  { key: 'green_depth', i18nKey: 'courses.crs.popup.green_depth', col: 0 },
  { key: 'hole_labels', i18nKey: 'courses.crs.popup.hole_labels', col: 0 },
  { key: 'shotgun_priority', i18nKey: 'courses.crs.popup.shotgun_priority', col: 0 },
  { key: 'pace_of_play', i18nKey: 'courses.crs.popup.pace_of_play', col: 0 },
  { key: 'default_tee_by_gender', i18nKey: 'courses.crs.popup.default_tee_by_gender', col: 0 },
  { key: 'tee_name_abbr', i18nKey: 'courses.crs.popup.tee_name_and_abbreviation', col: 1 },
  { key: 'default_tee_by_course', i18nKey: 'courses.crs.popup.master_roster_default_tees', col: 1 },
  { key: 'tee_color', i18nKey: 'courses.crs.popup.tee_color', col: 1 },
  { key: 'yardage', i18nKey: null, col: 1 },  // label comes from distance_units
  { key: 'tee_pace_of_play', i18nKey: 'courses.crs.popup.tee_pace_of_play', col: 1 },
  { key: 'tee_hole_labels', i18nKey: 'courses.crs.popup.tee_hole_labels', col: 1 },
]

export const Step2CopyData = ({
  copyInfo,
  onCopyInfoChange,
  existingCourseId,
  onExistingCourseChange,
  archiveOldCourse,
  onArchiveOldCourseChange,
  copyOptions,
  onCopyOptionChange,
}) => {
  const { data: existingCourses = [], isLoading: coursesLoading } = useGetExistingCoursesQuery(undefined, { skip: !copyInfo })
  const isMrLeague = useSelector((state) => state?.misc?.data?.MRLeague)

  const col0Options = COPY_OPTIONS.filter((o) => o.col === 0)
  const col1Options = COPY_OPTIONS.filter((o) => o.col === 1)

  const handleCheckAll = () => {
    COPY_OPTIONS.forEach((o) => onCopyOptionChange(o.key, true))
  }

  const handleUncheckAll = () => {
    COPY_OPTIONS.forEach((o) => onCopyOptionChange(o.key, false))
  }

  const renderOption = (option) => {
    const label = option.i18nKey
      ? window.I18n.t(option.i18nKey)
      : option.key === 'yardage' ? window.I18n.t('courses.crs.popup.yardage') : option.key

    const checked = !!copyOptions[option.key]
    return (
      <div key={option.key} className="fieldset inline check">
        <label className="btsp_icheck_label no-padding-left">
          <div className={`icheckbox_square-orange btsp-icheck-xs icheck-item icheck ${checked ? 'checked' : ''}`}>
            <input type="checkbox" className="icheck-input" checked={checked} onChange={() => onCopyOptionChange(option.key, !checked)} />
          </div>
          <span>{label}</span>
        </label>
      </div>
    )
  }

  return (
    <div className='add-margin-top-10'>
      <div className="btsp_notices">
        {window.I18n.t('courses.crs.popup.when_import_info_from_usga', { distance_units: window.I18n.t('courses.crs.popup.yardage') })}
      </div>

      <div className="customer_box courses-copy-info-box">
        <label className="less_important_text">
          {window.I18n.t('courses.crs.popup.want_to_copy_info')}
        </label>
        <div className="row no-margin courses-row-align-center">
          <div className="pull-left">
            <label className="btsp_icheck_label no-padding-left courses-icheck-label-top">
              <div className={`icheckbox_square-orange btsp-icheck-xs icheck-item icheck ${copyInfo ? 'checked' : ''}`}>
                <input type="checkbox" className="icheck-input" checked={copyInfo} onChange={() => onCopyInfoChange(!copyInfo)} />
              </div>
              <span>{window.I18n.t('courses.crs.popup.copy_data_from_existing_course')}</span>
            </label>
          </div>
          {copyInfo && (
            <div className="pull-right">
              {coursesLoading ? (
                <GlgInlineLoadingSpinner show={true} />
              ) : (
                <select
                  className="form-control courses-existing-course-select"
                  value={existingCourseId || ''}
                  onChange={(e) => onExistingCourseChange(e.target.value)}
                >
                  <option value="" disabled>{window.I18n.t('courses.crs.popup.select_course')}</option>
                  {existingCourses.map((course) => (
                    <option key={course.value} value={course.value}>{course.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      {copyInfo && existingCourseId && (
        <div className="customer_box btsp_square_solid extra_course_info course_tee_checkboxes">
          <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 no-padding">
            {col0Options.map(renderOption)}
          </div>
          <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 no-padding">
            {col1Options.map(renderOption)}
            <div className="fieldset inline check">
              <span
                className="course_tee_checkboxes_check_all courses-check-all-link"
                onClick={handleCheckAll}
              >
                {window.I18n.t('buttons.check_all')}
              </span>
              {' / '}
              <span
                className="course_tee_checkboxes_unckeck_all courses-check-all-link"
                onClick={handleUncheckAll}
              >
                {window.I18n.t('buttons.uncheck_all')}
              </span>
            </div>
          </div>
          <div className="clear" />
        </div>
      )}

      {copyInfo && isMrLeague && existingCourseId && (
        <div className="customer_box extra_course_info courses-archive-box">
          <label className="less_important_text">
            {window.I18n.t('courses.crs.popup.want_to_archive_old_course')}
          </label>
          <div className="row no-margin">
            <label className="btsp_icheck_label no-padding-left courses-icheck-label-top">
              <div className={`icheckbox_square-orange btsp-icheck-xs icheck-item icheck ${archiveOldCourse ? 'checked' : ''}`}>
                <input type="checkbox" className="icheck-input" checked={archiveOldCourse} onChange={() => onArchiveOldCourseChange(!archiveOldCourse)} />
              </div>
              <span className="small_font_size" title={window.I18n.t('courses.crs.popup.archive_info')}>
                {window.I18n.t('courses.crs.popup.archive_existing_course')}
                <i className="add-margin-left-5 gga-info-circle" />
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

Step2CopyData.propTypes = {
  copyInfo: PropTypes.bool.isRequired,
  onCopyInfoChange: PropTypes.func.isRequired,
  existingCourseId: PropTypes.string,
  onExistingCourseChange: PropTypes.func.isRequired,
  archiveOldCourse: PropTypes.bool.isRequired,
  onArchiveOldCourseChange: PropTypes.func.isRequired,
  copyOptions: PropTypes.object.isRequired,
  onCopyOptionChange: PropTypes.func.isRequired,
}
